// components/models/Veo31FastControlPanel.tsx
import React from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import Select from '../ui/Select';
import Switch from '../ui/Switch';
import { Veo31FastParams } from '#types/types';
import { useKernel } from '@/kernel/Kernel';

interface Veo31FastControlPanelProps {
  params: Veo31FastParams;
  setModelParams: (params: (prevParams: { [model: string]: Veo31FastParams }) => { [model: string]: Veo31FastParams }) => void;
  diffusionModel: string;
}

const defaultVeo31FastParams: Veo31FastParams = {
  prompt: '',
  negative_prompt: '',
  duration: 8,
  resolution: '720p',
  aspect_ratio: 'auto',
  generate_audio: false,
  seed: -1,
};

export const getDefaultVeo31FastParams = (): Veo31FastParams => ({ ...defaultVeo31FastParams });

// The endpoint takes a "4s" | "6s" | "8s" enum, converted from this number in app/api/film/route.ts.
const DURATION_OPTIONS = [
  { value: '4', label: '4s' },
  { value: '6', label: '6s' },
  { value: '8', label: '8s' },
];
const RESOLUTION_OPTIONS: NonNullable<Veo31FastParams['resolution']>[] = ['720p', '1080p', '4k'];
const ASPECT_RATIO_OPTIONS: NonNullable<Veo31FastParams['aspect_ratio']>[] = ['auto', '16:9', '9:16'];

const Veo31FastControlPanel: React.FC<Veo31FastControlPanelProps> = React.memo(({
  params, setModelParams, diffusionModel,
}) => {
  const { kernel } = useKernel();

  const handleParamChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' || type === 'range' ? parseFloat(value) : value;

    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Veo31FastControlPanel] handleParamChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: newValue,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      ...(name === 'prompt' || name === 'negative_prompt'
        ? { value_length: (newValue as string).length }
        : { value: newValue }),
      model_name: diffusionModel,
      source_component: 'Veo31FastControlPanel',
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Veo31FastControlPanel] handleSelectChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: name === 'duration' ? Number(value) : value,
        },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'Veo31FastControlPanel',
    });
  };

  const onSwitchGenerateAudio = () => {
    const newValue = !params.generate_audio;
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Veo31FastControlPanel] onSwitchGenerateAudio: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: { ...currentParams, generate_audio: newValue },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: 'generate_audio',
      value: newValue,
      model_name: diffusionModel,
      source_component: 'Veo31FastControlPanel',
    });
  };

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe the motion you want from the current frame..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <AutoGrowingTextarea
          label="Negative Prompt"
          placeholder="Properties to avoid."
          name="negative_prompt"
          value={params.negative_prompt ?? ''}
          onChange={handleParamChange}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <Select
          id="veo-3-1-fast-duration"
          label="Duration"
          value={String(params.duration)}
          options={DURATION_OPTIONS}
          onChange={(value) => handleSelectChange('duration', value)}
        />
      </div>

      <div className="mb-4">
        <Select
          id="veo-3-1-fast-resolution"
          label="Resolution"
          value={params.resolution}
          options={RESOLUTION_OPTIONS}
          onChange={(value) => handleSelectChange('resolution', value)}
        />
      </div>

      <div className="mb-4">
        <Select
          id="veo-3-1-fast-aspect-ratio"
          label="Aspect Ratio"
          value={params.aspect_ratio ?? 'auto'}
          options={ASPECT_RATIO_OPTIONS}
          onChange={(value) => handleSelectChange('aspect_ratio', value)}
        />
      </div>

      <div className="flex items-center space-x-2 mb-4">
        <Switch
          checked={params.generate_audio === true}
          onChange={onSwitchGenerateAudio}
          labelRight="Generate Audio"
        />
      </div>
    </>
  );
});

Veo31FastControlPanel.displayName = 'Veo31FastControlPanel';

export default Veo31FastControlPanel;
